import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import JsonEditor from '../../components/JsonEditor';
import CommandRow, { copy } from './CommandRow';
import { LABELS_TEXT } from '../SoftwarePolicy/policyEdit';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import {
  cveText,
  gapInfo,
  ruleText,
  sortedFindings,
  versionText,
} from './softwarePolicy';

// One machine against its account's software policy. `data` is the whole result of the installed-programs script and
// `drift` is the answer of cma/softwaredrift for it (made once by the machine page, which also gives the Software tab its
// CVEs): { state: 'idle' | 'loading' | 'ok' | 'error', result?, error? }. Nothing is changed on the machine; the
// commands are only shown.
function SoftwarePolicyTab({ data, drift, account }) {
  const [showRaw, setShowRaw] = useState(false);
  const state = drift || { state: 'loading' };
  if (state.state === 'loading' || state.state === 'idle') return <Stack alignItems="center" sx={{ py: 6 }}><CircularProgress /></Stack>;
  if (state.state === 'error') return <Alert severity="info">{state.error}</Alert>;
  if (state.result?.policy?.status === 'none') return <Alert severity="info">This account has no software policy yet.</Alert>;

  const result = state.result || {};
  const findings = sortedFindings(result.findings);
  const okItems = Array.isArray(result.okItems) ? result.okItems : [];
  const ruleSets = Array.isArray(result.ruleSets) ? result.ruleSets : [];

  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="center">
        {ruleSets.length > 0 && (
          <Tooltip title={ruleSets.map((r) => r.filterText).join('\n')}>
            <Chip size="small" label={`${ruleSets.length} rule set${ruleSets.length === 1 ? ' applies' : 's apply'}: ${ruleSets.map((r) => r.description || r.filterText).join(', ')}`} />
          </Tooltip>
        )}
        {result.labelsSource && result.labelsSource !== 'ves' && (
          <Tooltip title={LABELS_TEXT[result.labelsSource] || ''}>
            <Chip size="small" color="warning" variant="outlined" label="Machine labels not known" />
          </Tooltip>
        )}
        <Chip size="small" color={findings.length ? 'warning' : 'success'} variant="outlined" label={`${findings.length} to review`} />
        <Chip size="small" variant="outlined" label={`${okItems.length} compliant`} />
        {account && <Button component={Link} size="small" to={`/softwarepolicy?account=${encodeURIComponent(account)}`}>Edit the policy</Button>}
      </Stack>

      {findings.length === 0 ? (
        <Alert severity="success">Everything the policy asks about on this machine is in line with it.</Alert>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small" aria-label="Software policy findings">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Program</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Version</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Rule</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Details</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {findings.map((f, i) => {
                const info = gapInfo(f.gap);
                return (
                  <TableRow key={`${f.key}-${f.name}-${i}`} hover sx={{ verticalAlign: 'top' }}>
                    <TableCell>
                      <Tooltip title={info.meaning}>
                        <Chip size="small" color={info.color} label={info.label} />
                      </Tooltip>
                    </TableCell>
                    <TableCell>{f.name || f.key}</TableCell>
                    <TableCell>{versionText(f)}</TableCell>
                    <TableCell>
                      {ruleText(f)}
                      {f.reason && <Typography variant="caption" color="text.secondary" display="block">{f.reason}</Typography>}
                    </TableCell>
                    <TableCell>
                      <Stack spacing={0.5}>
                        {cveText(f) && <Chip size="small" color="warning" variant="outlined" label={cveText(f)} sx={{ alignSelf: 'flex-start' }} />}
                        {f.note && <Typography variant="body2">{f.note}</Typography>}
                        {f.proposedCheck && <CommandRow label="Check" command={f.proposedCheck} />}
                        {f.proposedApply && <CommandRow label="Apply" command={f.proposedApply} />}
                        {!f.proposedCheck && ['outdated', 'missing', 'forbidden-present', 'vulnerable'].includes(f.gap) && (
                          <Typography variant="caption" color="text.secondary">No script can do this yet.</Typography>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {okItems.length > 0 && (
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 700 }}>Compliant ({okItems.length})</Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small" aria-label="Compliant software">
              <TableBody>
                {okItems.map(item => (
                  <TableRow key={item.key}>
                    <TableCell>{item.key}</TableCell>
                    <TableCell>{item.state === 'absent' ? 'Not installed' : `Installed ${item.installed || ''}`.trim()}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      <Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <Button size="small" onClick={() => setShowRaw(!showRaw)}>{showRaw ? 'Hide raw JSON' : 'Raw JSON'}</Button>
          {showRaw && (
            <Tooltip title="Copy the JSON">
              <IconButton size="small" aria-label="Copy raw JSON" onClick={() => copy(JSON.stringify(result, null, 2))}><ContentCopyIcon fontSize="inherit" /></IconButton>
            </Tooltip>
          )}
        </Stack>
        {showRaw && (
          <Box sx={{ mt: 1 }}><JsonEditor value={JSON.stringify(result, null, 2)} label="Raw JSON" /></Box>
        )}
      </Box>
    </Stack>
  );
}

export default SoftwarePolicyTab;
